import {MemoParams} from 'module/Memo/hook/useMemoDq'
import {DqElement} from 'module/Dq/searchDq'
import {SetCb} from 'type/SetCb'
import {DragEvent, MouseEvent, RefObject} from 'react'
import {ByValueCache} from 'module/Slct/type/ByValueCache'
import {TextAlign} from 'type/TextAlign'

export interface SlctOption<ModelType extends object | void, ValueType extends string | number> extends DqElement<ValueType> {
	value?: ValueType | null
	active?: boolean
	looseMatch?: boolean
	model?: ModelType
}

export enum SlctFormat {
	ValueLabel = 'value - label',
	Value = 'value',
	Label = 'label',
	LabelValue = 'label (value)',
}

export type SlctOnChangeState = ({focus, inputValue}: { focus: boolean; inputValue: string }) => void

export interface SlctBaseProps<ModelType extends object | void, ValueType extends string | number> {
	options: SlctOption<ModelType, ValueType>[]
	byValueCache?: ByValueCache<ValueType>
	filterOptions: ((options: SlctOption<ModelType, ValueType>[], inputValue: string) => SlctOption<ModelType, ValueType>[]) | undefined // undefined disables search!
	menuIsOpen?: boolean
	setMenuIsOpen?: (open: boolean) => void
	preventCloseMenuOnSelect?: boolean
	menuWidth?: number
	menuHeight?: number
	disabled?: boolean
	loading?: boolean
	placeholderHTML?: string | null
	autoFocus?: boolean
	tabIndex?: number
	align?: TextAlign
	memo?: MemoParams
	onChangeState?: SlctOnChangeState
	refInput?: RefObject<HTMLInputElement | null>
	menuPortal?: HTMLElement | null
}

export interface SlctInternalProps<ModelType extends object | void, ValueType extends string | number> extends SlctBaseProps<ModelType, ValueType> {
	inputValue: string
	focus: boolean
	cursor: number | null
	setCursor: (value: number | null) => void
	onClickOption: (option: SlctOption<ModelType, ValueType>, evt: MouseEvent<HTMLDivElement>) => void
	onMoveOption?: (currentIndex: number, nextIndex: number, evt: DragEvent<HTMLDivElement>) => void
	refInput: RefObject<HTMLInputElement | null>
	displayFormat: SlctFormat
}

export interface SlctProps<ModelType extends object | void, ValueType extends string | number> extends SlctBaseProps<ModelType, ValueType> {
	value: ValueType | null | undefined
	onChange: SetCb<SlctOption<ModelType, ValueType> | null>
	showClearButton?: boolean
	displayFormat?: SlctFormat
}

export interface SlctLabelOnlyProps<ModelType extends object | void, ValueType extends string | number> extends SlctBaseProps<ModelType, ValueType> {
	value: ValueType | null | undefined
	onChange: SetCb<SlctOption<ModelType, ValueType> | null>
	/** only usable when null is not one of the available values */
	hideClearButton?: boolean
}

export interface SlctMultiProps<ModelType extends object | void, ValueType extends string | number> extends SlctBaseProps<ModelType, ValueType> {
	values: ValueType[]
	onChange: SetCb<SlctOption<ModelType, ValueType>[]>
	displayFormat?: SlctFormat
	disableManualSort?: boolean
}

export interface SlctOptionProps<ModelType extends object | void, ValueType extends string | number> extends SlctInternalProps<ModelType, ValueType> {
	index: number
}